import React, {useState, useEffect, useRef} from "react";
import {useHistory} from 'react-router-dom';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {BUTTON_PRIMARY, BUTTON_WARNING, PARCEL_USE_CASE, SUCCESS, TOAST_SUCCESS} from '../assets/constants/Constants';
import { getAndThenRemoveSecureItemFromSpecificStorage, getSecureItemFromSpecificStorage } from '../lib/BrowserStorageAccessMiddleware';
import { LOCAL_STORAGE, LS_CUSTOMER, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS } from '../assets/constants/BrowserStorageKeys';
import {LOCKER_OPEN_ICON} from '../assets/constants/Icons';
import {LANDING, LOCKER_OPENED, HOME, ACCESS_LOCKER_BANK} from '../assets/constants/PageList';
import {clearLocalStorageForUser, getPathForNextPage} from '../lib/Utils';
import {releaseLockerOfPickUpUser} from '../api/api';
import ButtonComponent from '../components/ButtonComponent';
import CircularProgressLoader from '../components/CircularProgressLoader';
import CustomDialog from '../components/CustomDialog';
import ShowToast from '../components/ToastComponent';

const LockerOpenedPage = () => {
    const history = useHistory();
    const {t} = useTranslation();
    const lockerUnitDetails = history.location.state?.lockerUnitDetails;
    const showReleaseLockerButton = history.location.state?.is_already_opened_once;
    const tenantAndAssignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const canReleaseLockerAutomaticallyAfterCountdown = tenantAndAssignmentSettings?.usecase_settings?.release_after_pickup_pwa?.value;
    const lockerReleaseConfirmationText = tenantAndAssignmentSettings?.locker_release_message ? tenantAndAssignmentSettings?.locker_release_message : t('are_you_sure_to_release_the_locker');
    const [isLoading, setIsLoading] = useState(false);
    const countdownTimerRef = useRef(null);
    const [remainingTimeInSecondsToReleaseLocker, setRemainingTimeInSecondsToReleaseLocker] = useState(8);
    const [isReleaseLockerModalVisible, setIsReleaseLockerModalVisible] = useState(false);
    const isPickUpButtonEnabled = tenantAndAssignmentSettings?.usecase_settings?.show_pickup_btn_pwa?.value;
    useEffect(() => {
        if (showReleaseLockerButton && canReleaseLockerAutomaticallyAfterCountdown) {
            let remainingTime = remainingTimeInSecondsToReleaseLocker;
            
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }

            countdownTimerRef.current = setInterval(() => {
                --remainingTime;

                if (remainingTime === 0) {
                    clearInterval(countdownTimerRef.current);
                    releaseButtonHandler();
                }
                else {
                    setRemainingTimeInSecondsToReleaseLocker(remainingTime);
                }
            }, 1000);
        }

        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        }
    }, []);

    const handleLogout = async () => {
        try {
            clearLocalStorageForUser(getAndThenRemoveSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_CUSTOMER))
            ShowToast(t('success_msg_successfully_logged_out'), TOAST_SUCCESS);
            setTimeout(() => {
                history.replace( '/');
            }, 100);
        } catch (error) {
            console.error('Error clearing app data.', error);
        }
    };

    const doneButtonHandler = () => {
        if (showReleaseLockerButton && canReleaseLockerAutomaticallyAfterCountdown) {
            clearInterval(countdownTimerRef.current);
        }
        if(isPickUpButtonEnabled && tenantAndAssignmentSettings?.usecase === PARCEL_USE_CASE) {
            handleLogout();
        }
        else if(!isPickUpButtonEnabled && tenantAndAssignmentSettings?.usecase !== PARCEL_USE_CASE) {
            history.replace({
                pathname: getPathForNextPage(LOCKER_OPENED, HOME)
            });
        }
    };

    const showReleaseLockerModal = () => {
        if (canReleaseLockerAutomaticallyAfterCountdown) {
            clearInterval(countdownTimerRef.current);
        }
        setIsReleaseLockerModalVisible(true);
    };

    const releaseButtonHandler = async () => {
        setIsLoading(true);

        try {
            const releaseLockerResponse = await releaseLockerOfPickUpUser(lockerUnitDetails?.ref_id);
            if (releaseLockerResponse.status === SUCCESS) {
                ShowToast(releaseLockerResponse.message, TOAST_SUCCESS);
                history.replace({
                    pathname: getPathForNextPage(LOCKER_OPENED, HOME)
                });
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while releasing locker: ', error);
        }
    };

    return (
        <Box className='locker-opened-page'>
            <Box className='main-container'>
                {isLoading && (
                    <Box className='main'>
                        <CircularProgressLoader message={t('Release')} />
                    </Box>
                )}
                {(!isLoading) && (
                    <Box className='main'>
                        <Box className='locker-open-icon-container'>
                            <LOCKER_OPEN_ICON />
                        </Box>
                        <Typography className='locker-unit'>{t('Locker_text')} {lockerUnitDetails?.locker_door?.door_no}</Typography>
                        <Typography className='close-door-instructions'>{t('Please_door_close')}</Typography>
                        <ButtonComponent buttonId='done-button' handleClick={doneButtonHandler} title={(showReleaseLockerButton && canReleaseLockerAutomaticallyAfterCountdown) ? t('Continue_button') : t('doneBtnText')} />
                        {(showReleaseLockerButton && ( !tenantAndAssignmentSettings?.is_pricing )) && (
                            <ButtonComponent
                                buttonId='release-button' 
                                type={BUTTON_WARNING} 
                                handleClick={showReleaseLockerModal} 
                                title={canReleaseLockerAutomaticallyAfterCountdown ? ` ${t('button_release')} (${remainingTimeInSecondsToReleaseLocker})`: t('button_release')}
                            />
                        )}
                        <CustomDialog
                            dialogVisible={isReleaseLockerModalVisible}
                            onClose={() => setIsReleaseLockerModalVisible(false)}
                            handleCancel={() => setIsReleaseLockerModalVisible(false)}
                            handleAccept={releaseButtonHandler}
                            dialogTitle={t('button_release_locker')}
                            dialogContentText={lockerReleaseConfirmationText}
                            buttonOneTitle={t('button_cancel')}
                            buttonTwoTitle={t('button_release')}
                            buttonProps={{buttonOne: {type: BUTTON_PRIMARY}, buttonTwo: {type: BUTTON_WARNING}}}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default LockerOpenedPage;